import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { DocumentService, DocumentByIdResponse, DocumentDetail, RenameComponentsInput } from 'src/app/modules/shared/services/document/document.service';
import { DocumentFoldersService } from 'src/app/modules/shared/services/document-folders/document-folders.service';
import { ClassificationReviewAISuggestions, ClassificationReviewDetails, ClassificationApprovalPayload } from 'src/app/modules/shared/services/classification-review/classification-review.service';

import { UserRoleService, UserRole } from
        'src/app/modules/shared/services/user-role/user-role.service';
import { Subscription } from 'rxjs';

interface CategoryOption { id: number; label: string; }
interface TypeOption     { id: number; label: string; }

export type FileSidebarMode = 'folders' | 'classification_review';

@Component({
  selector: 'app-file-sidebar',
  templateUrl: './file-sidebar.component.html',
  styleUrls: ['./file-sidebar.component.scss']
})
export class FileSidebarComponent implements OnInit, OnChanges, OnDestroy {
  canEditCategoryType = false;
  private roleSub?: Subscription;

  // Existing inputs/outputs for folders mode
  @Input() fileId: string | null = null;
  @Input() isOpen: boolean = false;
  @Output() close = new EventEmitter<void>();

  // New inputs/outputs for classification review mode
  @Input() mode: FileSidebarMode = 'folders';
  @Input() aiSuggestions: ClassificationReviewAISuggestions | null = null;
  @Input() documentDetails: ClassificationReviewDetails | null = null;
  @Output() onApprove = new EventEmitter<ClassificationApprovalPayload>();

  file: DocumentDetail | null = null;
  loading: boolean = false;
  error: string | null = null;
  viewLoading: boolean = false;

  isEditing = false;
  saving = false;
  editModel: Partial<DocumentDetail> = {};

  // Mode tracking
  isClassificationMode = false;

  categoryOptions: CategoryOption[] = [];
  typeOptions: TypeOption[] = [];

  constructor(
    private documentService: DocumentService,
    private documentFoldersService: DocumentFoldersService,
    private userRoleService: UserRoleService
  ) { }

  async ngOnInit(): Promise<void> {
    this.roleSub = this.userRoleService.userRole$
      .subscribe(role => this.canEditCategoryType = role === UserRole.MANAGER || role === UserRole.SUPERVISOR || role === UserRole.SUPER_ADMIN);

    this.isClassificationMode = this.mode === 'classification_review';

    // Show spinner while async data loads (categories, file details, types)
    this.loading = true;

    try {
      const categories = await this.documentFoldersService.getCategories();
      this.categoryOptions = categories.map(c => ({
        id: c.category_id,
        label: this.prettyCategory(c.category_name)
      }));

      if (this.isClassificationMode) {
        if (this.documentDetails && this.aiSuggestions) {
          await this.initializeClassificationReview();
        }
      } else {
        if (this.fileId) {
          await this.getFileById();
        }
      }
    } catch (err) {
      console.error('Error during initialization:', err);
      this.error = 'Failed to load data';
    } finally {
      this.loading = false;
    }
  }

ngOnChanges(changes: SimpleChanges): void {
  // If fileId changes in folders mode, reset editing state and fetch the file data.
  // Skip in classification_review mode -- ngOnInit handles initialization there.
  // We check this.mode directly (not this.isClassificationMode) because on the
  // first call ngOnChanges fires before ngOnInit, so the flag isn't set yet.
  if (changes['fileId'] && this.mode !== 'classification_review') {
    // Reset editing state when switching files
    this.isEditing = false;
    this.editModel = {};
    this.saving = false;
    this.error = null;
    
    // If fileId is not null, fetch the file data
    if (changes['fileId'].currentValue) {
      this.getFileById();
    }
  }
}

  ngOnDestroy(): void {
    this.roleSub?.unsubscribe();
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  async getFileById(): Promise<void> {
    if (!this.fileId) return;
    
    this.loading = true;
    this.error = null;
    
    try {
      const file_response = await this.documentService.getById(this.fileId);
      this.file = file_response.file;

      if (this.file.categoryId) {
        await this.loadTypesForCategory(this.file.categoryId);
      }
    } catch (err) {
      this.error = 'Failed to load file details';
      console.error('Error fetching file:', err);
    } finally {
      this.loading = false;
    }
  }

  async loadTypesForCategory(categoryId: number): Promise<void> {
    try {
      const allTypes = await this.documentFoldersService.getTypes(categoryId);
      
      this.typeOptions = allTypes.map(t => ({
        id: t.type_id,
        label: t.type_name
      }));
      
    } catch (err) {
      console.error('Error loading types:', err);
      this.typeOptions = [];
    }
  }

  async onCategoryChange(): Promise<void> {
    if (this.editModel.categoryId) {
      await this.loadTypesForCategory(this.editModel.categoryId);
      this.editModel.typeId = undefined;
    } else {
      this.typeOptions = [];
      this.editModel.typeId = undefined;
    }
    this.onEditChange();
  }

  /**
   * Initialize component for classification review mode
   * Maps classification review details to internal file format
   */
  private async initializeClassificationReview(): Promise<void> {
    if (!this.documentDetails || !this.aiSuggestions) return;

    // Extract file extension from original filename
    const extension = this.extractExtension(this.documentDetails.originalFilename);

    // Map classification review data to DocumentDetail format
    this.file = {
      fileId: this.documentDetails.documentId,
      originalFileName: this.documentDetails.originalFilename,
      generatedFileName: this.aiSuggestions.generatedFilename || this.documentDetails.originalFilename,
      categoryId: this.aiSuggestions.categoryId,
      typeId: this.aiSuggestions.typeId,
      owner: this.aiSuggestions.owner,
      issuer: this.aiSuggestions.issuer,
      detail: this.aiSuggestions.detail,
      documentDate: this.aiSuggestions.documentDate,
      taxYear: this.aiSuggestions.taxYear,
      extension: extension,
      uploadedAt: this.documentDetails.documentCreatedAt
    };

    // Load types for the AI-suggested category
    if (this.file.categoryId) {
      await this.loadTypesForCategory(this.file.categoryId);
    }

    // Auto-start edit mode for classification review
    this.startEdit();
  }

  /**
   * Extract file extension from filename
   */
  private extractExtension(filename: string): string {
    const lastDot = filename.lastIndexOf('.');
    return lastDot >= 0 ? filename.substring(lastDot + 1) : '';
  }

  /**
   * Build approval payload from edit model for classification review
   */
  private buildApprovalPayload(): ClassificationApprovalPayload {
    const payload: ClassificationApprovalPayload = {
      categoryId: this.editModel.categoryId,
      typeId: this.editModel.typeId,
      owner: this.editModel.owner,
      issuer: this.editModel.issuer,
      detail: this.editModel.detail,
      documentDate: this.editModel.documentDate,
      taxYear: this.editModel.taxYear,
      generatedFilename: this.editModel.generatedFileName
    };

    // Clean up undefined values
    Object.keys(payload).forEach(key => {
      if ((payload as any)[key] === undefined) {
        delete (payload as any)[key];
      }
    });

    return payload;
  }

  closePanel(): void {
    this.close.emit();
  }
  
  startEdit(): void {
    if (!this.file) return;
    this.isEditing = true;
    this.editModel = {
      ...this.file,
      // coerce for input controls
      taxYear: this.file.taxYear ? String(this.file.taxYear) : '',
      documentDate: this.file.documentDate ?? null,
    };
  }

  // called when user clicks Cancel
  cancelEdit(): void {
    this.isEditing = false;
    this.editModel = {};
  }

  // enable Save only if something changed
  hasChanges(): boolean {
    if (!this.file) return false;
    const changes = this.diff(this.file, this.editModel);
    // treat empty strings as "no value" to prevent false positives
    for (const k of Object.keys(changes)) {
      const v = (changes as any)[k];
      if (typeof v === 'string' && v.trim() === (this.file as any)[k]) {
        delete (changes as any)[k];
      }
    }
    return Object.keys(changes).length > 0;
  }

  onEditChange(): void {
  }

  private cleanPayload(changes: Partial<DocumentDetail>): RenameComponentsInput {
    const out: RenameComponentsInput = {};
    const put = (k: keyof RenameComponentsInput, v: any) => {
      if (v === undefined || v === null) return;
      if (typeof v === 'string') {
        const t = v.trim();
        if (!t) return;
        (out as any)[k] = t;
      } else {
        (out as any)[k] = v;
      }
    };

    put('categoryId', changes.categoryId);
    put('typeId', changes.typeId);
    put('owner', changes.owner);
    put('issuer', changes.issuer);
    put('detail', changes.detail);

    if (changes.taxYear !== undefined) {
      const ty = String(changes.taxYear).trim();
      if (/^\d{4}$/.test(ty)) out.taxYear = ty;
    }
    if (changes.documentDate !== undefined) {
      const dd = String(changes.documentDate).trim(); // already YYYY-MM-DD from input
      if (dd) out.documentDate = dd;
    }

    return out;
  }

  public prettyCategory(c?: string | null): string {
    if (!c) return 'N/A';
    return c
      .toLowerCase()
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  
  displayCategory(id?: number | null): string {
    if (!id) return 'N/A';
    const hit = this.categoryOptions.find(c => c.id === id);
    return hit ? hit.label : `Invalid-category, Please select a valid category`;   
  }

  displayType(id?: number | null): string {
    if (!id) return 'N/A';
    const hit = this.typeOptions.find(t => t.id == id); 
    return hit ? hit.label : `Invalid-type, Please select a valid type`;
  }

  async save(): Promise<void> {
    if (!this.fileId || !this.file) return;

    if (this.isClassificationMode) {
      // Classification review mode: emit approval event
      await this.approveClassification();
    } else {
      // Folders mode: existing save logic
      await this.saveFoldersMode();
    }
  }

  /**
   * Handle approval for classification review mode
   */
  private async approveClassification(): Promise<void> {
    this.saving = true;
    this.error = null;

    try {
      const payload = this.buildApprovalPayload();
      this.onApprove.emit(payload);
      // Component will be closed by parent handling the approval
    } catch (err) {
      this.error = 'Failed to approve classification';
      console.error(err);
      this.saving = false;
    }
  }

  /**
   * Save changes in folders mode (existing logic)
   */
  private async saveFoldersMode(): Promise<void> {
    if (!this.fileId || !this.file) return;

    const changes = this.diff(this.file, this.editModel);

    const payload = this.cleanPayload(changes);
    if (Object.keys(payload).length === 0) {
      this.isEditing = false;
      return;
    }

    if (!this.canEditCategoryType) {
      delete payload.categoryId;
      delete payload.typeId;
    }

    this.saving = true;
    this.error = null;
    try {
      await this.documentService.renameFileByComponents(this.fileId, payload);
      await this.getFileById();
      this.isEditing = false;
    } catch (err) {
      this.error = 'Failed to save changes';
      console.error(err);
    } finally {
      this.saving = false;
    }
  }

  private diff<T extends Record<string, any>>(a: T, b: Partial<T>): Partial<T> {
    const out: Partial<T> = {};
    for (const k of Object.keys(b)) {
      if (b[k as keyof T] !== a[k as keyof T]) {
        (out as any)[k] = b[k as keyof T];
      }
    }
    return out;
  }

  async downloadFile(): Promise<void> {
    if (!this.fileId || !this.file) {
      return;
    }

    try {
      const response = await this.documentService.getDocumentAccess(this.fileId, 'download');
      
      if (response && response.url) {
        const link = document.createElement('a');
        link.href = response.url;
        link.download = this.file.generatedFileName || this.file.originalFileName || '';
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error('Invalid response from download API');
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      this.error = 'Failed to download file';
    }
  }

  async viewFile(): Promise<void> {
    if (!this.fileId || !this.file) {
      return;
    }

    this.viewLoading = true;
    this.error = null;

    try {
      const response = await this.documentService.getDocumentAccess(this.fileId, 'view');
      
      if (response && response.url) {
        // Open the document in a new tab for viewing
        window.open(response.url, '_blank');
      } else {
        throw new Error('Invalid response from access API');
      }
    } catch (err) {
      console.error('Error viewing file:', err);
      this.error = 'Failed to view file';
    } finally {
      this.viewLoading = false;
    }
  }

  // This component was renamed from sidebar to modal, but we're keeping the file names
  // the same to avoid having to update imports throughout the application
}
